import { 
  collection, 
  getDocs, 
  query, 
  orderBy, 
  limit,
  startAfter,
  getCountFromServer,
  doc,
  getDoc,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';

export const thoughtsService = {
  // Get a random thought from Firestore
  async getRandomThought() {
    try {
      // Get total count of thoughts
      const thoughtsRef = collection(db, 'thoughts');
      const snapshot = await getCountFromServer(thoughtsRef);
      const totalThoughts = snapshot.data().count;
      
      if (totalThoughts === 0) {
        throw new Error('No thoughts found in database');
      }
      
      // Generate random offset
      const randomOffset = Math.floor(Math.random() * totalThoughts);
      
      // Get thoughts with random offset
      const thoughtsQuery = query(
        thoughtsRef,
        orderBy('id'),
        startAfter(randomOffset),
        limit(1)
      );
      
      const querySnapshot = await getDocs(thoughtsQuery);
      
      if (querySnapshot.empty) {
        // Fallback: get first thought if random offset didn't work
        const fallbackQuery = query(thoughtsRef, limit(1));
        const fallbackSnapshot = await getDocs(fallbackQuery);
        return fallbackSnapshot.docs[0].data();
      }
      
      return querySnapshot.docs[0].data();
    } catch (error) {
      console.error('Error fetching random thought:', error);
      // Return fallback thought
      return {
        id: '1-bruce-lee',
        thought: 'Be like water',
        author: 'Bruce Lee',
        application: 'It promotes being open-minded and capable of change rather than rigid, allowing for honest expression and resilience in life',
        summary: "Empty your mind, be formless, shapeless, like water. If you put water into a cup, it becomes the cup. You put water into a bottle and it becomes the bottle. You put it in a teapot it becomes the teapot. Now, water can flow or it can crash. Be water, my friend. Bruce Lee",
        url: 'https://www.youtube.com/watch?v=ER5Z_MyUkJQ&t=930s'
      };
    }
  },

  // Get multiple random thoughts
  async getRandomThoughts(count = 10) {
    try {
      const thoughtsRef = collection(db, 'thoughts');
      const snapshot = await getDocs(thoughtsRef);
      
      if (snapshot.empty) {
        throw new Error('No thoughts found in database');
      }
      
      const allThoughts = snapshot.docs.map(doc => doc.data());
      
      // Shuffle and return requested count
      const shuffledThoughts = this.shuffleArray(allThoughts);
      return shuffledThoughts.slice(0, Math.min(count, allThoughts.length));
    } catch (error) {
      console.error('Error fetching random thoughts:', error);
      
      // Check if it's a permissions error
      if (error.code === 'permission-denied' || error.message.includes('permissions')) {
        console.warn('Firestore permissions error. Please deploy Firestore rules or check your security rules in Firebase Console.');
        console.warn('To fix: Go to Firebase Console → Firestore → Rules and set allow read: if true for thoughts collection');
      }
      
      // Return fallback thoughts
      return [
        {
          id: '1-brucel-lee',
          thought: 'Be like water',
          author: 'Bruce Lee',
          application: 'It promotes being open-minded and capable of change rather than rigid, allowing for honest expression and resilience in life',
          summary: 'Empty your mind, be formless, shapeless, like water. If you put water into a cup, it becomes the cup. You put water into a bottle and it becomes the bottle. You put it in a teapot it becomes the teapot. Now, water can flow or it can crash. Be water, my friend. Bruce Lee',
          url: 'https://www.youtube.com/watch?v=ER5Z_MyUkJQ&t=930s'
        },
        {
          id: '2-success-plan',
          thought: 'Transform your life with a six months success plan',
          author: 'Motivational Speaker',
          application: 'Helps create structured approach to achieving long-term goals by breaking them into manageable daily actions',
          summary: 'Create a structured plan to achieve your goals over six months. Break down big dreams into manageable daily actions.',
          url: 'https://www.youtube.com/watch?v=3xkDW3Nf6mI&t=1034s'
        },
        {
          id: '3-criticism-worth',
          thought: "Criticism doesn't define your worth",
          author: 'Life Coach',
          application: 'Builds emotional resilience and self-confidence by separating external opinions from self-worth',
          summary: "Your value isn't determined by what others say about you. Focus on your own growth and self-improvement.",
          url: 'https://www.youtube.com/watch?v=W74-EBXZgwc&t=1442s'
        },
        {
          id: "4-improve-1-percent",
          thought: "Improve Yourself 1% Every Day",
          author: 'Personal Development Expert',
          application: 'Encourages consistent small improvements that compound over time for significant long-term growth',
          summary: "Small consistent improvements compound over time. Focus on getting 1% better each day rather than trying to make massive changes overnight.",
          url: "https://www.youtube.com/watch?v=KrO1qb8bA3Y&pp=0gcJCbIJAYcqIYzv"
        },
        {
          id: "5-connect-mentors",
          thought: "Attend Events Seek Mentors & Connect With Like Minded",
          author: 'Networking Expert',
          application: 'Builds valuable professional relationships and accelerates personal growth through mentorship',
          summary: "Surround yourself with people who inspire and challenge you. Find mentors who have achieved what you want to achieve.",
          url: "https://www.youtube.com/watch?v=5FIHuFlxJcI&t=543s"
        },
        {
          id: "6-trust-process",
          thought: "Keep Learning, Keep Improving & Trust The Process",
          author: 'Success Coach',
          application: 'Maintains motivation during challenging times by focusing on continuous improvement and long-term vision',
          summary: "Success is a journey, not a destination. Trust that consistent effort and learning will lead to results.",
          url: "https://www.youtube.com/watch?v=5FIHuFlxJcI&t=1140"
        },
        {
          id: "7-work-on-yourself",
          thought: "Work Hard On Yourself Than You Do On Your Job",
          author: 'Career Development Expert',
          application: 'Prioritizes personal development to increase value and opportunities in professional life',
          summary: "Invest in your personal development. The better you become, the more valuable you become to others.",
          url: "https://www.youtube.com/watch?v=bxRJVLMbAeA"
        },
        {
          id: "8-communication-skills",
          thought: "Ten Tips To Boost Your Communication Skills",
          author: 'Communication Expert',
          application: 'Enhances interpersonal relationships and professional effectiveness through better communication',
          summary: "Effective communication is key to success. Learn to express your ideas clearly and listen actively.",
          url: "https://www.youtube.com/watch?v=AgJN-Fv9Wdo"
        },
        {
          id: "9-daily-skills",
          thought: "Work Daily On Your Skills & Your Communication",
          author: 'Skill Development Coach',
          application: 'Builds competitive advantage through consistent daily practice and skill refinement',
          summary: "Consistent daily practice in your skills and communication will set you apart from the competition.",
          url: "https://youtube.com/watch?v=McEG9FGbL8Y&t=679"
        },
        {
          id: "10-stay-hungry",
          thought: "Stay Hungry Stay Foolish",
          author: 'Steve Jobs',
          application: 'Maintains curiosity and innovation mindset to continue growing and achieving breakthrough success',
          summary: "Maintain your curiosity and willingness to learn. Don't let success make you complacent.",
          url: "https://www.youtube.com/watch?v=UF8uR6Z6KLc"
        }
      ];
    }
  },

  // Get a specific thought by ID
  async getThoughtById(thoughtId) {
    try {
      const thoughtRef = doc(db, 'thoughts', thoughtId);
      const thoughtSnap = await getDoc(thoughtRef);
      
      if (thoughtSnap.exists()) {
        return thoughtSnap.data();
      } else {
        throw new Error('Thought not found');
      }
    } catch (error) {
      console.error('Error fetching thought by ID:', error);
      // Return fallback thought
      return {
        id: 'fallback',
        text: 'Be like water',
        youtubeLink: 'https://www.youtube.com/watch?v=ER5Z_MyUkJQ&t=930s'
      };
    }
  },

  // Get all thoughts (for fallback)
  async getAllThoughts() {
    try {
      const thoughtsRef = collection(db, 'thoughts');
      const snapshot = await getDocs(thoughtsRef);
      
      if (snapshot.empty) {
        throw new Error('No thoughts found in database');
      }
      
      return snapshot.docs.map(doc => doc.data());
    } catch (error) {
      console.error('Error fetching all thoughts:', error);
      return [];
    }
  },

  // Delete a thought by ID
  async deleteThought(thoughtId) {
    try {
      const thoughtRef = doc(db, 'thoughts', thoughtId);
      await deleteDoc(thoughtRef);
      console.log('Thought deleted successfully:', thoughtId);
      return true;
    } catch (error) {
      console.error('Error deleting thought:', error);
      throw error;
    }
  },

  // Get all thoughts ordered by creation date (latest first)
  async getAllThoughtsOrdered() {
    try {
      const thoughtsRef = collection(db, 'thoughts');
      const q = query(thoughtsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        throw new Error('No thoughts found in database');
      }
      
      return snapshot.docs.map(doc => doc.data());
    } catch (error) {
      console.error('Error fetching ordered thoughts:', error);
      return [];
    }
  },

  // Utility function to shuffle array
  shuffleArray(array) {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }
};
